import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Search, CheckCircle, AlertCircle, Clock, RefreshCw, Download, Calendar, FileText } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { getAllTasks } from '../lib/api';
import { TranscriptionResponse } from '../lib/types';
export function History() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [transcriptions, setTranscriptions] = useState<TranscriptionResponse[]>([]);
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    fetchTranscriptions();
  }, []);
  const fetchTranscriptions = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await getAllTasks();
      const sortedTasks = response.tasks.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      setTranscriptions(sortedTasks);
    } catch (err) {
      console.error('Error fetching transcriptions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load transcriptions');
    } finally {
      setIsLoading(false);
    }
  };
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };
  const truncateText = (text: string, maxLength: number = 80) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };
  const filteredTranscriptions = transcriptions.filter(item => {
    if (searchTerm !== '' && !item.title?.toLowerCase().includes(searchTerm.toLowerCase()) && !item.video_id?.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }
    if (filterStatus && item.status !== filterStatus) {
      return false;
    }
    return true;
  });
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle size={16} className="text-green-500" data-id="element-2197" />;
      case 'processing':
      case 'pending':
        return <Clock size={16} className="text-blue-500" data-id="element-2198" />;
      case 'failed':
        return <AlertCircle size={16} className="text-red-500" data-id="element-2199" />;
      default:
        return null;
    }
  };
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className="flex items-center gap-1 text-xs font-medium text-success bg-success/10 px-2 py-0.5 rounded-full" data-id="element-2200">
            <CheckCircle size={12} data-id="element-2201" />
            <span data-id="element-2202">Completed</span>
          </span>;
      case 'processing':
      case 'pending':
        return <span className="flex items-center gap-1 text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full" data-id="element-2203">
            <Clock size={12} className="animate-spin" data-id="element-2204" />
            <span data-id="element-2205">Processing</span>
          </span>;
      case 'failed':
        return <span className="flex items-center gap-1 text-xs font-medium text-destructive bg-destructive/10 px-2 py-0.5 rounded-full" data-id="element-2206">
            <AlertCircle size={12} data-id="element-2207" />
            <span data-id="element-2208">Failed</span>
          </span>;
      default:
        return null;
    }
  };
  return <div className="container py-8" data-id="element-2209">
      <div className="flex justify-between items-center mb-8" data-id="element-2210">
        <div data-id="element-2211">
          <h1 className="text-3xl font-bold" data-id="element-2212">Transcription History</h1>
          <p className="text-muted-foreground mt-1" data-id="element-2213">
            View and manage your past transcriptions
          </p>
        </div>
        <Button variant="outline" onClick={fetchTranscriptions} className="flex items-center gap-2" disabled={isLoading} data-id="element-2214">
          <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} data-id="element-2215" />
          Refresh
        </Button>
      </div>
      <Card className="mb-6" data-id="element-2216">
        <CardContent className="p-4" data-id="element-2217">
          <div className="flex flex-col md:flex-row gap-4 justify-between" data-id="element-2218">
            <div className="relative flex-1" data-id="element-2219">
              <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" data-id="element-2220" />
              <Input className="pl-10" placeholder="Search by title or video ID" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-2221" />
            </div>
            <div className="flex gap-2 overflow-x-auto" data-id="element-2222">
              <Button variant={filterStatus === null ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus(null)} className="whitespace-nowrap" data-id="element-2223">
                All
              </Button>
              <Button variant={filterStatus === 'completed' ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus('completed')} className="whitespace-nowrap" data-id="element-2224">
                <CheckCircle size={14} className="mr-1" data-id="element-2225" />
                Completed
              </Button>
              <Button variant={filterStatus === 'processing' ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus('processing')} className="whitespace-nowrap" data-id="element-2226">
                <Clock size={14} className="mr-1" data-id="element-2227" />
                Processing
              </Button>
              <Button variant={filterStatus === 'failed' ? 'default' : 'outline'} size="sm" onClick={() => setFilterStatus('failed')} className="whitespace-nowrap" data-id="element-2228">
                <AlertCircle size={14} className="mr-1" data-id="element-2229" />
                Failed
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      {isLoading ? <div className="space-y-4" data-id="element-2230">
          {[...Array(5)].map((_, i) => <Card key={i} className="animate-pulse" data-id="element-2231">
              <CardContent className="p-4" data-id="element-2232">
                <div className="flex justify-between items-center" data-id="element-2233">
                  <div className="space-y-2 flex-1" data-id="element-2234">
                    <div className="h-4 bg-secondary/40 rounded w-3/4" data-id="element-2235"></div>
                    <div className="h-3 bg-secondary/40 rounded w-1/2" data-id="element-2236"></div>
                  </div>
                  <div className="w-24 h-8 bg-secondary/40 rounded" data-id="element-2237"></div>
                </div>
              </CardContent>
            </Card>)}
        </div> : error ? <Card className="border-destructive/20 bg-destructive/5" data-id="element-2238">
          <CardContent className="p-8 flex flex-col items-center text-center" data-id="element-2239">
            <AlertCircle size={32} className="text-destructive mb-4" data-id="element-2240" />
            <h2 className="text-xl font-semibold mb-2" data-id="element-2241">
              Failed to Load History
            </h2>
            <p className="text-muted-foreground mb-6" data-id="element-2242">{error}</p>
            <Button onClick={fetchTranscriptions} data-id="element-2243">Retry</Button>
          </CardContent>
        </Card> : filteredTranscriptions.length > 0 ? <div className="space-y-4" data-id="element-2244">
          {filteredTranscriptions.map(item => <Card key={item.task_id} className="overflow-hidden" data-id="element-2245">
              <CardContent className="p-0" data-id="element-2246">
                <div className="flex flex-col md:flex-row md:items-center" data-id="element-2247">
                  <div className="p-4 flex-1" data-id="element-2248">
                    <div className="flex items-center gap-2 mb-1" data-id="element-2249">
                      {getStatusIcon(item.status)}
                      <span className="font-medium" data-id="element-2250">
                        {item.title || `Untitled (${item.video_id?.substring(0, 8) || 'Unknown'})`}
                      </span>
                    </div>
                    <div className="flex flex-col md:flex-row md:items-center gap-1 md:gap-4 text-xs text-muted-foreground" data-id="element-2251">
                      <div className="flex items-center gap-1" data-id="element-2252">
                        <Calendar size={12} data-id="element-2253" />
                        <span data-id="element-2254">{formatDate(item.created_at)}</span>
                      </div>
                      {item.video_id && <div className="flex items-center gap-1" data-id="element-2255">
                          <FileText size={12} data-id="element-2256" />
                          <span data-id="element-2257">ID: {item.video_id}</span>
                        </div>}
                    </div>
                  </div>
                  <div className="flex items-center justify-end p-3 md:p-4 bg-card border-t md:border-t-0 md:border-l border-border/40" data-id="element-2258">
                    {item.status === 'completed' ? <Button variant="outline" size="sm" className="mr-2 bg-background hover:bg-background/90" onClick={() => navigate(`/app/transcriptions/${item.task_id}`)} data-id="element-2259">
                        <Download size={14} className="mr-1" data-id="element-2260" />
                        Download
                      </Button> : <div className="px-3 py-1.5 mr-2" data-id="element-2261">
                        {getStatusBadge(item.status)}
                      </div>}
                    <Button size="sm" className="bg-background hover:bg-background/90" onClick={() => navigate(`/app/transcriptions/${item.task_id}`)} data-id="element-2262">
                      View
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>)}
        </div> : <Card data-id="element-2263">
          <CardContent className="p-12 text-center" data-id="element-2264">
            <div className="text-gray-400 mb-4" data-id="element-2265">
              <Search size={48} className="mx-auto" data-id="element-2266" />
            </div>
            <h3 className="text-lg font-medium mb-1" data-id="element-2267">No results found</h3>
            <p className="text-gray-500" data-id="element-2268">
              Try adjusting your search or create a new transcription
            </p>
            <Link to="/app" data-id="element-2269">
              <Button className="mt-4" data-id="element-2270">New Transcription</Button>
            </Link>
          </CardContent>
        </Card>}
    </div>;
}